import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import StudentRankingSection from "../Instructor/sections/StudentRankingSection";
import {
  setupRankingSocketListeners,
  removeRankingListeners,
  joinBatch,
} from "@/store/ranking/rankingSocket";
import {
  selectTopLeader,
  selectNextBatches,
  selectBatchEntries,
  selectTotalBatches,
  selectFirstNameOfTopScorer,
  selectLastNameOfTopScorer,
} from "@/store/ranking/rankingSelectors";
import { motion } from "framer-motion";
import { TopRankState } from "./TopRankState";
import { Target, TrendingUp, Trophy, Users } from "lucide-react";
import {
  fetchPlacedStudents,
  fetchNonTechStudents,
} from "@/store/placements/placementSlice";
import PlacementMarquee from "./PlacementMarquee";
import LeaderboardLoader from "./LeaderBoardLoder";
import SocialMediaAds from "./SocialMediaAds";
import { RootState } from "@/types";

export default function ResizableSections() {
  const dispatch = useDispatch();

  const { students, nonTechStudents } = useSelector(
    (state: RootState) => state.placement
  );

  React.useEffect(() => {
    dispatch(fetchPlacedStudents());
    dispatch(fetchNonTechStudents());
  }, [dispatch]);

  React.useEffect(() => {
    setupRankingSocketListeners(dispatch);
    joinBatch();
    return () => removeRankingListeners();
  }, [dispatch]);

  const topLeader =
    useSelector((state: RootState) => selectTopLeader(state)) || {};
  const nextBatches = useSelector((state: RootState) =>
    selectNextBatches(state)
  );
  const batchEntries = useSelector((state: RootState) =>
    selectBatchEntries(state)
  );
  const totalBatches = useSelector((state: RootState) =>
    selectTotalBatches(state)
  );
  const firstNameOfTopScorer = useSelector((state: RootState) =>
    selectFirstNameOfTopScorer(state)
  );
  const lastNameOfTopScorer = useSelector((state: RootState) =>
    selectLastNameOfTopScorer(state)
  );


  // To divide equally, defaultSize = 100 / totalBatches
  const numBatches =
    typeof totalBatches === "number" && totalBatches > 0 ? totalBatches : 1;
  const panelSize = 100 / numBatches;

  const [showLoader, setShowLoader] = React.useState(true);

  React.useEffect(() => {
    if (firstNameOfTopScorer) {
      setTimeout(() => setShowLoader(false), 800);
    }
  }, [firstNameOfTopScorer]);

  return (
    <main className="min-h-screen scroller-none p-2 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
        <div className="absolute top-20 right-20 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 left-20 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        />
      </div>
      <PlacementMarquee
        techStudents={students}
        nonTechStudents={nonTechStudents}
      />

      {showLoader ? (
        <LeaderboardLoader loadingComplete={!!firstNameOfTopScorer} />
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-extrabold ">
              The Great Ranking Battle ⚔️
            </h1>
            <p className="text-gray-600 mt-2 text-sm md:text-base">
              Students are giving their all to claim the{" "}
              <span className="font-semibold text-yellow-500">#1 Rank</span> —
              every score counts, every point matters!
            </p>
          </motion.div>

          {/* Stats Cards */}

          <div className="flex items-center justify-center p-6">
            <motion.div
              className="relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              {/* Soft glowing background */}
              <motion.div
                className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br 
      from-emerald-200/40 via-cyan-200/40 to-emerald-200/40 blur-2xl opacity-60"
                animate={{
                  opacity: [0.45, 0.6, 0.45],
                  scale: [1, 1.04, 1],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12"
          >
            <TopRankState
              icon={<Users className="w-5 h-5" />}
              label="Total Students"
              value={topLeader.totalStudents?.toString()}
              color="from-blue-500 to-indigo-600"
            />
            <TopRankState
              icon={<Target className="w-5 h-5" />}
              label="Average Score"
              // value={(batchEntries.reduce((acc, d) => acc + d.totalScore, 0) / batchEntries.length).toFixed(1)}
              value={topLeader.averageScore?.toFixed(1)}
              color="from-purple-500 to-pink-600"
            />
            <TopRankState
              icon={<TrendingUp className="w-5 h-5" />}
              label="Top Score"
              // value={Math.max(...batchEntries.map((d) => d.totalScore)).toFixed(1)}
              value={topLeader.topScore?.toFixed(1)}
              color="from-emerald-500 to-green-600"
            />
            <TopRankState
              icon={<Trophy className="w-5 h-5" />}
              label="Leader"
              value={`${firstNameOfTopScorer}. ${lastNameOfTopScorer}`}
              color="from-orange-500 to-red-600"
            />
          </motion.div>

          <div className="relative flex items-center justify-center my-4">
            <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
            <div className="relative bg-white px-4 py-1 rounded-full shadow-sm animate-pulse text-sm text-gray-600">
              🛑 Next Batches:{" "}
              {nextBatches
                ?.map((b, i) => `${i + 1}: ${b.courseName} (${b.startTime})`)
                .join(", ")}
            </div>
          </div>
          <SocialMediaAds />
          <ResizablePanelGroup
            direction="horizontal"
            className="rounded-lg overflow-hidden"
          >
            {batchEntries.map(([batchName, students], index) => (
              <React.Fragment key={batchName}>
                <ResizablePanel defaultSize={panelSize}>
                  <StudentRankingSection
                    batchName={batchName}
                    topic={students.topic}
                    students={students.leaderboard}
                  />
                </ResizablePanel>

                {/* Add handle between all panels except the last */}
                {index < totalBatches - 1 && (
                  <ResizableHandle className=" rounded-xl transition-all duration-300 bg-gradient-to-b  via-gray-300 " />
                )}
              </React.Fragment>
            ))}
          </ResizablePanelGroup>
        </>
      )}
    </main>
  );
}
